import * as React from 'react';
import classNames from 'classnames';
import styles from './ViewBackground.scss';
import equal from 'fast-deep-equal/react';
import { isEmptyObject } from '~/utils/utils';
import { settings } from '~/utils/settings';
import VideoBackground from '~/components/VideoBackground/VideoBackground';
import { useAvailableSound } from '~/hooks/useAvailableSound';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade } from 'swiper/modules';
import type { Swiper as SwiperInstance } from 'swiper/types';
import { RootState, useAppSelector } from '~/Store';
import isDisabledAnimation from '~/hooks/isDisabledAnimation';

const stateSelector = (state: RootState) => {
    return {
        port: state.app.port,
        isTrusted: state.app.isTrusted,
        bundles: state.app.bundles,
        currentPage: state.app.currentPage,
    };
};

interface IGalleryViewBackground {
    isBlur: boolean;
    bundle: IBundle;
    category: ICategory;
}

const GalleryViewBackground = ({ isBlur, bundle, category }: IGalleryViewBackground) => {
    const state = useAppSelector(stateSelector, equal);
    let currentBundle = bundle;
    if (bundle?.parentBundleId) {
        currentBundle = state.bundles[bundle.parentBundleId];
    }
    const [swiper, setSwiper]: [SwiperInstance, (sw: SwiperInstance) => void] = React.useState(null);

    const [isBlurView, setBlur] = React.useState(false);
    const isAvailableSoundEffect = useAvailableSound(category, currentBundle);
    let muted = !isAvailableSoundEffect;
    if (!state.isTrusted) {
        muted = true;
    }

    const _isDisabledAnimation = isDisabledAnimation(bundle.name);

    React.useEffect(() => {
        setBlur(isBlur);
    }, [isBlur]);

    React.useEffect(() => {
        swiper?.slideTo(state.currentPage?.bgGallerySlideIndex);
    }, [state.currentPage]);

    if (state.port?.isVisible) {
        return null;
    }

    const backgroundColor = bundle?.backgroundColor || category?.backgroundColor;
    const hasBackgroundMask = !!backgroundColor;
    const style = !!backgroundColor ? ({ '--bundle-gallery-background-color': backgroundColor } as React.CSSProperties) : {};

    const classesWrapper = classNames(styles.galleryBackgroundWrapper, {
        [styles.blur]: isBlurView,
        [styles.galleryWithMask]: hasBackgroundMask,
    });

    const gallery = !!bundle.gallery?.length ? bundle.gallery : [];

    return (
        <div className={classesWrapper} style={style}>
            <Swiper
                onSwiper={(swiper: SwiperInstance) => {
                    setSwiper(swiper);
                }}
                className={styles.galleryBackgroundSlider}
                slidesPerView={1}
                initialSlide={state.currentPage?.bgGallerySlideIndex || 0}
                updateOnWindowResize={true}
                effect="fade"
                modules={[EffectFade]}
            >
                {gallery.map((_, index) => {
                    if (!gallery[index].video || isEmptyObject(gallery[index].video) || _isDisabledAnimation) {
                        const itemStyles = {
                            backgroundImage: `url(${gallery[index].image})`,
                        };
                        return <SwiperSlide className={styles.galleryBackgroundImage} style={itemStyles} key={`slide_${index}`} />;
                    }

                    return (
                        <SwiperSlide key={`slide_${index}`} className={styles.galleryVideoBackground}>
                            <VideoBackground
                                muted={(state.currentPage?.bgGallerySlideIndex || 0) !== index || muted}
                                poster={gallery[index].image}
                                video={gallery[index].video}
                                posterClassName={styles.galleryPoster}
                                className={classNames(state.currentPage?.name, styles[state.currentPage?.name], styles[settings.realm], styles.galleryVideo, {
                                    [styles.videoBackground]: !!backgroundColor,
                                })}
                                withMask={!!backgroundColor}
                            >
                                {!!backgroundColor && <div className={styles.videoBackgroundMask}></div>}
                            </VideoBackground>
                        </SwiperSlide>
                    );
                })}
                <div className={classNames(styles.backgroundMask, { [styles.visibleMask]: hasBackgroundMask })} />
            </Swiper>
        </div>
    );
};

export default React.memo(GalleryViewBackground);
